import {
  DeleteOutlined,
  EditTwoTone,
  FolderViewOutlined,
  MailOutlined,
} from "@ant-design/icons";
import {
  Button,
  Descriptions,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import Column from "antd/lib/table/Column";
import React from "react";
import { Link } from "react-router-dom";
import { LocalStorageUser } from "../../interfaces/user.interface";
import Timeago from "../shared/timeago/timeago";

interface UserTableProps {
  users: LocalStorageUser[];
  visibleAddressModal: (addresses: any[]) => void;
  visibleOrdersModal: (userId: string) => void;
  onDeleteUser: (userId: string) => void;
  deletingUserId: string | null;
}

interface ExpandedRowProps {
  name: string;
  _id: string;
  email: string;
  phone?: string;
  createDate?: string;
  role?: string;
  userType?: string;
}

const getUserTypeLabel = (record: any) => {
  const raw = (record?.role || record?.userType || "").toLowerCase();

  if (
    raw.includes("owner") ||
    raw.includes("store") ||
    raw.includes("merchant")
  ) {
    return { label: "Store Owner", color: "gold" };
  }

  if (raw.includes("admin")) {
    return { label: "Admin", color: "purple" };
  }

  if (raw.includes("customer") || raw.includes("user")) {
    return { label: "Customer", color: "blue" };
  }

  return { label: "User", color: "default" };
};

const compareText = (a?: string, b?: string) => {
  return (a || "").localeCompare(b || "", "tr", { sensitivity: "base" });
};

const compareDate = (a?: string, b?: string) => {
  const first = a ? new Date(a).getTime() : 0;
  const second = b ? new Date(b).getTime() : 0;
  return first - second;
};

const ExpandedRow: React.FC<ExpandedRowProps> = ({
  name,
  _id,
  email,
  phone,
  createDate,
  role,
  userType,
}) => {
  const typeInfo = getUserTypeLabel({ role, userType });

  return (
    <Descriptions layout="vertical" size="small" column={2}>
      <Descriptions.Item label="Type">
        <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Name">{name || "-"}</Descriptions.Item>
      <Descriptions.Item label="Email">{email || "-"}</Descriptions.Item>
      <Descriptions.Item label="Phone">{phone || "-"}</Descriptions.Item>
      <Descriptions.Item label="Created At">
        {createDate ? <Timeago date={createDate} /> : "-"}
      </Descriptions.Item>
      <Descriptions.Item label="User Id">{_id}</Descriptions.Item>
    </Descriptions>
  );
};

const UserTable: React.FC<UserTableProps> = ({
  users,
  visibleAddressModal,
  visibleOrdersModal,
  onDeleteUser,
  deletingUserId,
}) => {
  const { md } = useBreakpoint();

  return (
    <div className="user-table-wrapper">
      <Table
        dataSource={users || []}
        rowKey="_id"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        className="premium-users-table"
        expandable={
          !md
            ? {
                expandedRowRender: (record: any) => <ExpandedRow {...record} />,
              }
            : undefined
        }
        scroll={{ x: 900 }}
      >
        <Column
          ellipsis
          title="Name"
          dataIndex="name"
          sorter={(a: any, b: any) => compareText(a.name, b.name)}
          sortDirections={["ascend", "descend"]}
          render={(name: string, record: any) => {
            const typeInfo = getUserTypeLabel(record);

            return (
              <div className="user-name-cell">
                <div className="user-name-main">{name || "-"}</div>
                <Tag color={typeInfo.color} className="user-type-tag">
                  {typeInfo.label}
                </Tag>
              </div>
            );
          }}
        />

        <Column
          ellipsis
          title="Email"
          dataIndex="email"
          sorter={(a: any, b: any) => compareText(a.email, b.email)}
          sortDirections={["ascend", "descend"]}
          render={(email: string) => email || "-"}
        />

        <Column
          ellipsis
          title="Phone"
          dataIndex="phone"
          sorter={(a: any, b: any) => compareText(a.phone, b.phone)}
          sortDirections={["ascend", "descend"]}
          render={(phone: string) => phone || "-"}
        />

        {md && (
          <Column
            ellipsis
            title="Created At"
            dataIndex="createDate"
            width={170}
            sorter={(a: any, b: any) => compareDate(a.createDate, b.createDate)}
            sortDirections={["ascend", "descend"]}
            render={(createDate: string) => {
              return createDate ? <Timeago date={createDate} /> : "-";
            }}
          />
        )}

        <Column
          ellipsis
          title="Addresses"
          dataIndex="addresses"
          width={110}
          align="center"
          render={(addresses: any[]) => (
            <Tooltip title="Show addresses">
              <Button
                type="text"
                icon={<FolderViewOutlined />}
                onClick={() => visibleAddressModal(addresses || [])}
              />
            </Tooltip>
          )}
        />

        <Column
          ellipsis
          title="Orders"
          dataIndex="_id"
          width={90}
          align="center"
          render={(userId: string) => (
            <Tooltip title="Show orders">
              <Button
                type="text"
                icon={<FolderViewOutlined />}
                onClick={() => userId && visibleOrdersModal(userId)}
              />
            </Tooltip>
          )}
        />

        <Column
          ellipsis
          title="Actions"
          key="actions"
          align="center"
          width={180}
          render={(_: any, record: any) => (
            <Space size="middle">
              <Tooltip title="Edit user">
                <Link to={"/users/edit/" + record._id}>
                  <EditTwoTone className="fs-18" />
                </Link>
              </Tooltip>

              <Tooltip title="Send bulk notification">
                <Link to={"/bulk-notification/" + record._id}>
                  <MailOutlined className="fs-18" />
                </Link>
              </Tooltip>

              <Popconfirm
                title="Kullanıcıyı kaldır"
                okText="Evet"
                cancelText="Vazgeç"
                onConfirm={() => onDeleteUser(record._id)}
              >
                <Tooltip title="Delete user">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deletingUserId === record._id}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
    </div>
  );
};

export default UserTable;
